import { type QueryFunctionContext, useQuery } from "@tanstack/react-query";
import { type BasePerformanceRequestFilterParams } from "features/Library/Common/models";
import { lineChartFactory, type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import { CHART_PERIOD, type ChartPeriod } from "hooks/useChartPeriodMeasure";

import * as contentReportingService from "features/Reporting/services/contentReportingService";

type QueryContext = QueryFunctionContext<[_key: string, filter: BasePerformanceRequestFilterParams]>;

type QueryContextPeriod = QueryFunctionContext<
  [_key: string, filter: BasePerformanceRequestFilterParams, period: ChartPeriod]
>;

const getLinkLineChart = async ({ queryKey, signal }: QueryContextPeriod) => {
  const [_key, filter, period] = queryKey;
  const { data } = await contentReportingService.getLinksDailyData(
    {
      aggregation: period,
      ...filter,
    },
    signal!,
  );

  const formattedData = lineChartFactory(data);

  return {
    date: formattedData.Date,
    clicks: formattedData.TotalClicks,
    pageVisits: formattedData.TotalPageVisits,
    cumulativeClicks: formattedData.CumulativeClicks,
    cumulativePageVisits: formattedData.CumulativePageVisits,
  };
};

const getLinkEngagement = async ({ queryKey, signal }: QueryContext) => {
  const [_key, filter] = queryKey;
  const { data } = await contentReportingService.getLinksEngagement(filter, signal!);

  const barData: DataPoint[] = [
    {
      id: "pageVisits",
      category: "Impressions",
      value: data.TotalPageVisits,
      fillColor: reportingColors.yellow,
    },
    {
      id: "clicks",
      category: "Clicks",
      value: data.TotalClicks,
      fillColor: reportingColors.greenDark,
    },
  ];

  const cards: CardReportingItem[] = [
    {
      statDescription: "Impressions",
      stat: data.TotalPageVisits.toLocaleString(),
      popupBody: "The total number of impressions.",
    },
    {
      statDescription: "Clicks",
      stat: data.TotalClicks.toLocaleString(),
      popupBody: "The total number of clicks.",
    },
    {
      statDescription: "Click-Through Rate",
      stat: data.TotalClickRate * 100,
      popupBody: "The percentage of impressions that resulted in clicks.",
      isStatPercentage: true,
    },
  ];

  return {
    barData,
    cards,
  };
};

const getLinksOverview = async ({ queryKey, signal }: QueryContext) => {
  const [_key, filter] = queryKey;
  const { data } = await contentReportingService.getLinksOverview(filter, signal!);
  return data;
};

const getLinkDetails = async ({ queryKey, signal }: QueryContext) => {
  const [_key, filter] = queryKey;
  const { data } = await contentReportingService.getLinksDetails(filter, signal!);
  return data;
};

export const useLinkLineChartQuery = (
  filter: PerformanceWithAccountFilter & { linkId?: number },
  period: ChartPeriod,
) => {
  return useQuery({
    queryKey: ["LinkLineChart", filter, period],
    queryFn: getLinkLineChart,
    enabled: period !== CHART_PERIOD.UNSET && filter.type !== undefined,
  });
};
export const useLinkEngagementQuery = (
  filter: PerformanceWithAccountFilter & { linkId?: number },
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["LinkEngagement", filter],
    queryFn: getLinkEngagement,
    enabled: enabled && filter.type !== undefined,
  });
};

export const useLinksOverviewQuery = (filter: PerformanceWithAccountFilter, enabled: boolean) => {
  return useQuery({
    queryKey: ["LinksOverviewReportDetails", filter],
    queryFn: getLinksOverview,
    enabled: enabled && filter.type !== undefined,
  });
};
export const useLinkDetailsQuery = (filter: PerformanceWithAccountFilter & { linkId?: number }, enabled: boolean) => {
  return useQuery({
    queryKey: ["LinksReportDetails", filter],
    queryFn: getLinkDetails,
    enabled: enabled && filter.type !== undefined,
  });
};
